import { Center, Loader, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import type { AppModule, PermissionAction, Role } from '@mexp/shared';
import { ForbiddenPage } from '@/features/errors/ErrorPages';
import { INTERNAL } from '@/lib/paths';
import { useAuth } from './AuthProvider';

export function FullPageLoader({ label = 'Loading…' }: { label?: string }) {
  return (
    <Center h="100dvh">
      <Stack align="center" gap="sm">
        <Loader />
        <Text c="dimmed" size="sm">
          {label}
        </Text>
      </Stack>
    </Center>
  );
}

/** Signed-in users only; users with a temporary password are sent to change it first. */
export function RequireAuth({ children, loginPath = '/login' }: { children: ReactNode; loginPath?: string }) {
  const { status, user } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <FullPageLoader />;
  if (status === 'anonymous' || !user) {
    const next = `${location.pathname}${location.search}`;
    const isHome = next === '/' || next === INTERNAL;
    return <Navigate to={`${loginPath}${isHome ? '' : `?next=${encodeURIComponent(next)}`}`} replace />;
  }
  if (user.mustChangePassword && location.pathname !== '/change-password') return <Navigate to="/change-password" replace />;
  return <>{children}</>;
}

/** The staff app: client (exporter) logins are sent to their portal. */
export function RequireStaff({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user?.userType !== 'staff') return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * The client portal: staff are sent to the same page under /internal, which also keeps
 * links from before the move (e.g. /requests/<id>) working for them.
 */
export function RequireClient({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user?.userType !== 'exporter') {
    const path = location.pathname === '/' ? '' : location.pathname;
    return <Navigate to={`${INTERNAL}${path}${location.search}${location.hash}`} replace />;
  }
  return <>{children}</>;
}

/** Shows a friendly 403 when the matrix (or role list) does not allow the page. */
export function RequirePermission({
  module,
  action = 'view',
  roles,
  children,
}: {
  module?: AppModule;
  action?: PermissionAction;
  roles?: Role[];
  children: ReactNode;
}) {
  const { can, hasRole } = useAuth();
  if ((module && !can(module, action)) || (roles && !hasRole(...roles))) return <ForbiddenPage />;
  return <>{children}</>;
}
